<template>
  <div>
    <navigation-top/>

    <v-container fluid>
      <v-alert v-if="message" color="success mb-5">
        {{ message }}
      </v-alert>
      <v-alert v-if="error" color="error mb-5">
        {{ error }}
      </v-alert>
    </v-container>

    <v-container v-if="currentUser" wrap class="ma-0 px-0 py-16" fluid>
      <v-row no-gutters>
        <v-col>
          <v-container fluid class="header">
            <v-row>
              <v-col>
                <div class="d-flex align-center">
                  <v-btn
                    class="mr-2"
                    icon="mds:arrow-back"
                    variant="text"
                    @click="goBack()"
                  />
                  <h2 class="mb-2 text-h2">Profile</h2>
                </div>
                <p class="text-h6 text-grey-darken-2 mb-5">Update your photo and personal info</p>
              </v-col>
            </v-row>
          </v-container>

          <v-card v-if="!editing" class="mx-auto profile d-flex flex-column">
            <v-card-text class="flex-grow-1 ml-2">
              <v-row>
                <v-col class="px-0 py-0">
                  <h3 class="text-h4">{{ currentUser.firstname + ' ' + currentUser.lastname }}</h3>

                  <v-list>
                    <v-list-item>
                      <div class="profile-pic-container">
                        <v-avatar class="profile-pic" size="100">
                          <div v-if="currentUser.picture && currentUser.picture.url" class="pic-container">
                            <img :src="currentUser.picture.url" alt="Profile Picture" class="pic">
                          </div>
                          <div v-else class="pic-container">
                            <v-icon color="grey" size="100">mdi-account-circle</v-icon>
                          </div>
                        </v-avatar>
                      </div>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-title>Email:</v-list-item-title>
                      <div class="text-grey-darken-1 text-body-1">{{ currentUser.email }}</div>
                    </v-list-item>
                    <v-list-item v-if="currentUser.phoneNumber">
                      <v-list-item-title>Phone Number:</v-list-item-title>
                      <div class="text-grey-darken-1 text-body-1">{{ currentUser.phoneNumber }}</div>
                    </v-list-item>
                    <v-list-item v-if="currentUser.job">
                      <v-list-item-title>Job:</v-list-item-title>
                      <div class="text-grey-darken-1 text-body-1">{{ currentUser.job }}</div>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Company:</v-list-item-title>
                      <div class="text-grey-darken-1 text-body-1">{{ currentUser.company }}</div>
                    </v-list-item>
                  </v-list>
                </v-col>
                <div class="cardActions">
                  <v-menu
                    location="bottom right"
                    :offset="[8, 0]"
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        variant="text"
                        icon="mdi-dots-vertical"
                      />
                    </template>

                    <v-list density="compact">
                      <v-list-item @click="editing = true" prepend-icon="mdi-pencil">
                        <v-list-item-title>Edit Profile</v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="dialog = true" prepend-icon="mds:lock-reset">
                        <v-list-item-title>Change Password</v-list-item-title>
                      </v-list-item>

                      <v-list-item @click="logout" prepend-icon="mds:logout">
                        <v-list-item-title>Logout</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card v-if="editing" @keydown.enter="updateProfile" class="mx-auto edit-profile">
            <v-container>
              <v-form ref="form" v-model="valid">
                <v-container>
                  <v-row class="pb-2">
                    <v-col cols="10" class="pa-0">
                      <div class="edit-profile-pic-container">
                        <v-avatar class="profile-pic" size="150">
                          <div v-if="picture !== null && picture.url !== null" class="pic-container">
                            <img :src="picture.url" alt="Profile Picture" class="pic"
                                 @click="showDropdown = !showDropdown">
                            <div>
                              <v-menu v-model="showDropdown" class="drop-down-menu">
                                <template v-slot:activator="{ props }">
                                  <div class="camera-icon" v-bind="props">
                                    <v-icon color="white">mdi-camera</v-icon>
                                  </div>
                                </template>
                                <v-list>
                                  <v-list-item @click="openFilePicker">Change Profile Picture</v-list-item>
                                  <v-list-item @click="deletePicture">Delete Profile Picture</v-list-item>
                                </v-list>
                              </v-menu>
                            </div>
                          </div>
                          <div v-else class="pic-container">
                            <v-icon color="grey" size="150" @click="openFilePicker">mdi-account-circle</v-icon>
                            <div class="camera-icon" @click="openFilePicker">
                              <v-icon color="white">mdi-camera</v-icon>
                            </div>
                          </div>
                          <input
                            type="file"
                            ref="fileInput"
                            accept="image/*"
                            style="display: none;"
                            @change="handleFileUpload"
                          />
                        </v-avatar>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row class="pb-2">
                    <v-col cols="6" class="pa-0">
                      <v-text-field
                        id="first-name"
                        color="secondary"
                        autofocus
                        name="firstname"
                        label="First Name"
                        validate-on="blur"
                        v-model="firstname"
                        :maxLength="40"
                        :rules="[rules.required]"
                        hint="At most 40 characters"
                        variant="outlined"
                        density="default"
                      ></v-text-field>
                    </v-col>
                    <v-col col="6" class="pa-0 ml-4">
                      <v-text-field
                        id="last-name"
                        color="secondary"
                        autofocus
                        label="Last Name"
                        validate-on="blur"
                        v-model="lastname"
                        :maxLength="40"
                        :rules="[rules.required]"
                        hint="At most 40 characters"
                        variant="outlined"
                        density="default"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="pb-2">
                    <v-col class="pa-0" cols="6">
                      <v-text-field
                        id="phone-number-input"
                        color="secondary"
                        autofocus
                        label="Phone Number"
                        validate-on="blur"
                        v-model="phoneNumber"
                        :rules="[rules.phoneNumber]"
                        variant="outlined"
                        density="default"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="pb-2">
                    <v-col class="pa-0" cols="6">
                      <v-text-field
                        id="company"
                        color="secondary"
                        autofocus
                        label="Company"
                        validate-on="blur"
                        v-model="company"
                        maxLength="40"
                        variant="outlined"
                        density="default"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row class="pb-2">
                    <v-col class="pa-0" cols="6">
                      <v-text-field
                        id="job-title"
                        color="secondary"
                        autofocus
                        label="Team/Job Title"
                        validate-on="blur"
                        v-model="job"
                        maxLength="40"
                        variant="outlined"
                        density="default"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-form>
            </v-container>
            <v-card-actions class="justify-end">
              <v-btn rounded="lg" size="large" variant="outlined" color="gray" @click="closeEditing">Cancel
              </v-btn>
              <v-btn rounded="lg" size="large" variant="flat" color="#363a40" :loading="loading"
                     @click="updateProfile">Save
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-dialog v-model="dialog" max-width="450px">
            <v-card>
              <v-container fluid justify-space-between align-top class="d-flex flex-row">
                <v-card-title class="flex-grow-1">
                  <h5 class="text-h5">Change password</h5>
                </v-card-title>
                <div class="flex-shrink-1">
                  <v-btn variant="text" icon color="grey" @click="dialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </div>
              </v-container>
              <v-container grid-list-md class="py-0">
                <v-form ref="form" v-model="valid">
                  <v-container class="py-0" wrap>
                    <v-text-field
                      color="secondary"
                      name="password"
                      label="Old password"
                      v-model="oldpass"
                      :append-inner-icon="e1 ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append-inner="() => (e1 = !e1)"
                      :type="e1 ? 'password' : 'text'" min="8"
                      :rules="[rules.required]"
                      hint="At least 8 characters" counter
                      variant="outlined"
                      density="default"
                    ></v-text-field>
                    <v-text-field
                      color="secondary"
                      name="newpassword"
                      label="New password"
                      v-model="newpass"
                      :append-inner-icon="e2 ? 'mdi-eye-off' : 'mdi-eye'"
                      @click:append-inner="() => (e2 = !e2)"
                      :type="e2 ? 'password' : 'text'"
                      min="8"
                      :rules="[rules.required]"
                      hint="At least 8 characters, requires a mix of characters and numbers"
                      counter
                      variant="outlined"
                      density="default"
                    ></v-text-field>
                  </v-container>
                </v-form>
              </v-container>
              <v-card-actions class="justify-end pt-4">
                <v-btn rounded="lg" size="large" variant="outlined" color="gray" @click="dialog = false">Cancel</v-btn>
                <v-btn rounded="lg" size="large" variant="flat" color="#363a40" id="change-password"
                       @click="updatePassword">Change
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Storage from '@/storage'
import store from '@/store'
import NavigationTop from '@/components/NavigationTop.vue'
import { mapState } from 'vuex'
import { authHelpers } from '@/api/authHelpers'

export default {
  components: { NavigationTop },
  data () {
    return {
      editing: false,
      dialog: false,
      valid: true,
      loading: false,
      showDropdown: false,
      pictureDeletionRequested: false,
      oldpass: '',
      newpass: '',
      error: null,
      message: null,
      id: '',
      firstname: '',
      lastname: '',
      email: '',
      phoneNumber: null,
      company: null,
      job: '',
      picture: null,
      oldPicKey: '',
      storage: null,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        },
        phoneNumber: (value) => {
          const pattern = /^\+[1-9]\d{1,14}$/
          return !value || pattern.test(value) || 'Invalid phone number. Must start with a + sign followed by country code and numbers without spaces or punctuation'
        }
      },
      e1: true,
      e2: true,
      currentUser: null
    }
  },
  computed: {
    ...mapState({
      stateUser: state => state.user.user
    })
  },
  methods: {
    logout () {
      authHelpers.logoutAndClearStore()
      this.$nextTick(function () {
        this.$router.push({ path: '/login' })
      })
    },
    async updatePassword () {
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        this.$cognitoAuth.changePassword(this.oldpass, this.newpass, (err, result) => {
          if (err) {
            this.error = 'Incorrect password. If you created your account using Google, please reset your password on the Login page.'
            console.log(err)
          } else {
            this.updateMessage('Password change successful.')
            this.dialog = false
          }
        })
      }
    },
    updateMessage (newMessage) {
      this.message = newMessage
      setTimeout(() => {
        this.message = ''
      }, 5000)
    },
    async updateProfile () {
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        this.loading = true

        try {
          await this.$cognitoAuth.updateAttributes(this.firstname, this.lastname, this.phoneNumber, this.company)

          this.error = null

          if (this.pictureDeletionRequested) {
            this.picture = {
              name: null,
              url: null,
              file: null,
              mimeType: null
            }
          }
          await this.updateUser()

          if (this.oldPicKey) {
            await this.deletePictureFile(this.oldPicKey)
          }
          this.updateMessage('Profile updated successfully.')
          this.closeEditing()
        } catch (err) {
          this.error = err.message
        } finally {
          this.loading = false
        }
      } else {
        this.error = 'One or more fields are invalid.'
      }
    },
    // Calls apollo mutation that updates user attributes in the store
    async updateUser () {
      const variables = {
        firstname: this.firstname,
        lastname: this.lastname,
        email: this.email,
        phoneNumber: this.phoneNumber,
        company: this.company,
        picture: this.picture,
        job: this.job,
        increaseLoginCounter: false
      }
      await store.dispatch('user/updateUser', variables)
    },
    openFilePicker () {
      this.$refs.fileInput.click()
    },
    async handleFileUpload (event) {
      this.error = ''
      const file = event.target.files[0]

      if (this.pictureDeletionRequested) {
        this.pictureDeletionRequested = false
      }

      if (!this.picIsEmpty(this.picture)) {
        this.oldPicKey = this.picture.file.key
      }

      try {
        const response = await this.storage.uploadFile(
          `profile-pictures/${this.id}/${file.name}`,
          file
        )
        const { bucket, key } = response

        const downloadUrl = await this.storage.getFile(key)

        this.picture = {
          name: file.name,
          url: downloadUrl,
          file: {
            region: Storage.region,
            bucket,
            key
          },
          mimeType: file.type
        }
        this.showDropdown = false
      } catch (e) {
        console.log(e)
        this.error = 'Failed to upload file'
      } finally {
        event.target.value = null
        if (!this.error) {
          store.commit('snackbar/showMessage', { content: 'Picture uploaded' }, { root: true })
        }
      }
    },
    async deletePictureFile (key) {
      // Delete previous profile picture
      try {
        await this.storage.deleteFile(key)
      } catch (error) {
        console.error('Error deleting previous profile picture:', error)
        this.error = 'An error occurred while uploading file'
      }
    },
    deletePicture () {
      this.oldPicKey = this.picture.file.key
      this.picture = null
      this.pictureDeletionRequested = true
    },
    picIsEmpty (pic) {
      // true if picture is an empty object or null
      return (pic && !pic.url) || !pic
    },
    closeDialog () {
      this.oldpass = ''
      this.newpass = ''
      this.error = ''
      this.valid = true
    },
    closeEditing () {
      this.error = ''
      this.editing = false
      this.pictureDeletionRequested = false
      this.showDropdown = false
      this.oldPicKey = ''
      this.valid = true
    },
    goBack () {
      if (this.$router?.options?.history?.state?.back) {
        this.$router.push(this.$router?.options?.history?.state?.back)
      } else {
        this.$router.push('/dashboard')
      }
    },
    updatePreSignedURL (user) {
      this.currentUser = JSON.parse(JSON.stringify(user))

      if (this.currentUser?.picture && this.currentUser?.picture?.file && this.currentUser?.picture?.file?.key) {
        this.storage
          .getFile(this.currentUser.picture.file.key)
          .then((url) => {
            this.currentUser.picture.url = url
          })
      }
    }
  },
  watch: {
    dialog (val) {
      val || this.closeDialog()
    },
    editing (val) {
      if (val) {
        this.firstname = this.currentUser.firstname
        this.lastname = this.currentUser.lastname
        this.email = this.currentUser.email
        this.phoneNumber = this.currentUser.phoneNumber
        this.company = this.currentUser.company
        this.job = this.currentUser.job
        if (!this.picIsEmpty(this.currentUser.picture)) {
          this.picture = {
            name: this.currentUser.picture.name,
            url: this.currentUser.picture.url,
            file: {
              region: this.currentUser.picture.file.region,
              bucket: this.currentUser.picture.file.bucket,
              key: this.currentUser.picture.file.key
            },
            mimeType: this.currentUser.picture.mimeType
          }
        }
        this.id = this.currentUser.id
      }
    },
    stateUser (updatedUser) {
      if (updatedUser) {
        this.updatePreSignedURL(updatedUser)
      }
    }
  },
  mounted () {
    this.storage = new Storage()
    this.updatePreSignedURL(this.stateUser)
  }
}
</script>

<style scoped>
.header {
  max-width: 600px
}

.drop-down-menu .v-menu__content.v-menu__content--active {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.edit-profile-pic-container {
  position: relative;
  width: 150px;
  height: 150px;
  margin-top: 30px;
  margin-bottom: 50px;
  margin-left: 10px;
  overflow: hidden;
  border-radius: 50%;
}

.profile-pic-container {
  width: 100px;
  height: 100px;
}

.pic-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.profile-pic {
  position: relative;
  overflow: hidden;
}

.pic {
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
}

.camera-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px;
  size: 20px;
  border-radius: 50%;
  cursor: pointer;
  z-index: 1;
}

.cardActions {
  gap: 16px;
  top: 10px;
  right: 8px;
  display: flex;
  position: absolute;
}

.profile {
  max-width: 600px;
  max-height: 500px;
  border-radius: 12px;
  background: #FCFCFC;
  padding: 12px 16px 16px;
  border: 1px solid #FCFCFC;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.edit-profile {
  max-width: 800px;
  border-radius: 12px;
  background: #FCFCFC;
  padding: 12px 16px 16px;
  border: 1px solid #FCFCFC;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

div.error {
  color: red;
  background: none !important;
  border: none;
}

.required:after {
  content: " *";
  color: red;
}
</style>
